import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { CartLineItem, hashCartItem } from '@lib/entities/Cart';
import { Order, Reward } from '@lib/entities';
import { Menu } from '@lib/entities/Menu';
import { DietSpec } from '@lib/entities/Nutrition';
import { OrderSummary, PromoCodeStatus, Quote, Quotes, Tip } from '@lib/entities/Payment';
import { HangReward } from '@lib/entities/Rewards';
import { CurrencyInteger, toCurrencyInteger } from '@lib/types/currency';
import { UserInfo } from '@lib/entities/Address';
import { loggedOut } from '@lib/redux/actions/lifecycle';
import { type Deferred, type DeferredAction } from '@lib/redux/deferred';

export type QuoteUpdateSource =
    | 'applyReward'
    | 'applyCredit'
    | 'applyHangReward'
    | 'applyPromoCode'
    | 'applyGiftcard';

interface CartConfig {
    includeUtensils: boolean;
    promoCode?: string;
}

/** How a menu re-map changed the cart. See `CartState.menuChangeReport`. */
export interface MenuChangeReport {
    /** An item from the order is not on the new store's menu. */
    missingMenuItem: boolean;
    /** Base price differs from the price the item was added at. NOT a failure. */
    menuPriceChange: boolean;
    /** Name of a customization ingredient that is no longer available, if any. */
    removedCustomizationName?: string;
    /** A baseline ingredient was dropped from a built item. */
    baselineDropped: boolean;
    /** Nothing survived the re-map. */
    cartEmptied: boolean;
}

export const EmptyMenuChangeReport: MenuChangeReport = {
    missingMenuItem: false,
    menuPriceChange: false,
    removedCustomizationName: undefined,
    baselineDropped: false,
    cartEmptied: false
};

export interface CartState {
    cartItems: CartLineItem[];
    cartConfig: CartConfig;
    hasCarbonOffset: boolean;
    pendingReorder?: Order;
    activeDiet?: DietSpec;
    promoCode?: string;
    promoCodeStatus?: PromoCodeStatus;
    summary?: OrderSummary;
    tip?: Tip;

    quoteIsLoading: boolean;
    quote?: Quote;
    /**
     * JSAD-7263: set when a quote attempt ended WITHOUT a usable quote for a reason the
     * user cannot see (a 2xx body that is not a quote, a parser throw, no schedule store).
     * Every such path used to exit silently: no toast, no Sentry, and a Pay button that
     * stayed grey with no explanation. Checkout renders this with a retry control in the
     * totals slot. Cleared when the next quote attempt starts (`setQuoteIsLoading(true)`)
     * and when a quote lands (`setQuote(<quote>)`).
     */
    quoteError?: string;
    cartItemErrors: string[];
    // Set by updateCartForNewMenuSaga: true when switching to a store whose menu
    // removed an item, changed a price, or dropped a customization/baseline
    // ingredient. The checkout store-switch reads this to decide whether to route
    // through the legacy location -> menu -> checkout flow instead of staying on
    // checkout. See useCheckoutStoreSwitch.
    menuChangeRequiresReview: boolean;
    /**
     * Per-flag breakdown of the same menu re-map (JSAD-6335). Fast Reorder needs a
     * verdict that EXCLUDES price change (a price change is allowed to proceed, with
     * the new total shown on the confirm screen), which the boolean above folds in.
     */
    menuChangeReport: MenuChangeReport;
    /**
     * True only while a Fast Reorder pre-flight is running. Read by
     * updateCartForNewMenuSaga to replace rather than append the pending reorder, and
     * to stay silent so the fast path owns user messaging.
     */
    isFastReorder: boolean;
    rewards: Reward[];
    creditRewards: Reward[];
    eligibleRewardIds: string[];
    appliedRewards: Reward[];
    appliedHangRewards: HangReward[];
    credit: CurrencyInteger;
    appliedCredit: CurrencyInteger;
    appliedGiftcards?: number[];
    quoteUpdateSource?: QuoteUpdateSource;
    goToCheckoutSignal?: string;
    /**
     * JSAD-6595: ties every add-on click in this cart to the order it
     * produces. One UUID v4 per cart, minted lazily (first click or payment,
     * whichever comes first). Cleared by `resetCart` (successful order) so
     * the next cart mints a fresh one — a failed payment keeps the cart and
     * therefore keeps the id. Redux state is not persisted; a reload clears
     * the cart and the id together, which is correct.
     */
    cartCorrelationId?: string;

    // web only
    userInfo?: UserInfo;
}

export interface UpdateQuoteParams {
    resetQuote?: boolean;
}

const ZeroIntegerDollars = toCurrencyInteger(0);

/**
 * The checkout-derived fields a sign-out clears without touching the bag — the Zustand
 * `DefaultCheckoutState` (lib/state/Cart.ts:159) that `resetRewards()` restores.
 */
const DefaultCheckoutState: Pick<
    CartState,
    | 'quote'
    | 'summary'
    | 'rewards'
    | 'creditRewards'
    | 'eligibleRewardIds'
    | 'appliedRewards'
    | 'appliedHangRewards'
    | 'credit'
    | 'appliedCredit'
    | 'userInfo'
> = {
    quote: undefined,
    summary: undefined,
    rewards: [],
    creditRewards: [],
    eligibleRewardIds: [],
    appliedRewards: [],
    appliedHangRewards: [],
    credit: ZeroIntegerDollars,
    appliedCredit: ZeroIntegerDollars,
    userInfo: undefined
};

const initialState: CartState = {
    cartItems: [],
    cartConfig: {
        includeUtensils: false
    },
    hasCarbonOffset: false,
    quoteIsLoading: false,
    cartItemErrors: [],
    menuChangeRequiresReview: false,
    menuChangeReport: EmptyMenuChangeReport,
    isFastReorder: false,
    rewards: [],
    creditRewards: [],
    eligibleRewardIds: [],
    appliedRewards: [],
    appliedHangRewards: [],
    credit: ZeroIntegerDollars,
    appliedCredit: ZeroIntegerDollars
};

const cartSlice = createSlice({
    name: 'cart',
    initialState,
    reducers: {
        addToCart: (state, action: PayloadAction<CartLineItem>) => {
            const item = action.payload;
            const newItemHash = hashCartItem(item);
            const existingIndex = state.cartItems.findIndex((e) => hashCartItem(e) === newItemHash);

            if (existingIndex < 0) {
                state.cartItems.push(item);
            } else {
                state.cartItems[existingIndex].quantity += item.quantity;
            }
        },
        removeFromCart: (state, action: PayloadAction<CartLineItem | string>) => {
            const itemOrId = action.payload;
            const id = typeof itemOrId === 'string' ? itemOrId : itemOrId.id;
            const index = state.cartItems.findIndex((e) => e.id === id);

            if (index >= 0) {
                if (typeof itemOrId === 'string') {
                    state.cartItems.splice(index, 1);
                } else {
                    state.cartItems[index].quantity = Math.max(
                        0,
                        state.cartItems[index].quantity - itemOrId.quantity
                    );
                    if (state.cartItems[index].quantity === 0) {
                        state.cartItems.splice(index, 1);
                    }
                }
            }
        },
        updateCartItem: (state, action: PayloadAction<CartLineItem>) => {
            const updated = action.payload;
            const index = state.cartItems.findIndex((item) => item.id === updated.id);
            if (index !== -1) {
                state.cartItems[index] = { ...state.cartItems[index], ...updated };
            }
        },
        setCartConfig: (state, action: PayloadAction<Partial<CartConfig>>) => {
            state.cartConfig = { ...state.cartConfig, ...action.payload };
        },
        toggleCarbonOffset: (state, action: PayloadAction<boolean>) => {
            state.hasCarbonOffset = action.payload;
        },
        setPendingReorder: (state, action: PayloadAction<Order | undefined>) => {
            state.pendingReorder = action.payload;
        },
        setQuoteIsLoading: (state, action: PayloadAction<boolean>) => {
            state.quoteIsLoading = action.payload;
            // A new attempt supersedes the previous failure; the saga re-sets it if the
            // attempt fails again.
            if (action.payload) {
                state.quoteError = undefined;
            }
        },
        setQuote: (state, action: PayloadAction<Quote | undefined>) => {
            state.quote = action.payload;
            if (action.payload) {
                state.quoteError = undefined;
            }
        },
        setQuoteError: (state, action: PayloadAction<string | undefined>) => {
            state.quoteError = action.payload;
        },
        resetCart: () => initialState,
        /** lib/state/Cart.ts:910 — rewards/credit/quote/guest-info back to defaults, bag kept. */
        resetRewards: (state) => {
            Object.assign(state, DefaultCheckoutState);
        },
        // ... other reducers for rewards, credit, etc.
        setAppliedRewards: (state, action: PayloadAction<Reward[]>) => {
            state.appliedRewards = action.payload;
        },
        setAppliedHangRewards: (state, action: PayloadAction<HangReward[]>) => {
            state.appliedHangRewards = action.payload;
        },
        setAppliedCredit: (state, action: PayloadAction<CurrencyInteger>) => {
            state.appliedCredit = action.payload;
        },
        setCredit: (state, action: PayloadAction<CurrencyInteger>) => {
            state.credit = action.payload;
        },
        setSummary: (state, action: PayloadAction<OrderSummary | undefined>) => {
            state.summary = action.payload;
        },
        setEligibleRewardIds: (state, action: PayloadAction<string[]>) => {
            state.eligibleRewardIds = action.payload;
        },
        setRewards: (state, action: PayloadAction<Reward[]>) => {
            state.rewards = action.payload;
        },
        setCreditRewards: (state, action: PayloadAction<Reward[]>) => {
            state.creditRewards = action.payload;
        },
        setPromoCodeStatus: (state, action: PayloadAction<PromoCodeStatus | undefined>) => {
            state.promoCodeStatus = action.payload;
        },
        setCartItemErrors: (state, action: PayloadAction<string[]>) => {
            state.cartItemErrors = action.payload;
        },
        setMenuChangeRequiresReview: (state, action: PayloadAction<boolean>) => {
            state.menuChangeRequiresReview = action.payload;
        },
        setMenuChangeReport: (state, action: PayloadAction<MenuChangeReport>) => {
            state.menuChangeReport = action.payload;
        },
        setIsFastReorder: (state, action: PayloadAction<boolean>) => {
            state.isFastReorder = action.payload;
        },
        setTip: (state, action: PayloadAction<Tip | undefined>) => {
            state.tip = action.payload;
        },
        setAppliedGiftcards: (state, action: PayloadAction<number[] | undefined>) => {
            state.appliedGiftcards = action.payload;
        },
        // Apply (add) a gift card AND record it as the newest quote-affecting
        // action, so the conflict detector in updateQuoteSaga can revert it when
        // it collides with an applied credit (credit + gift card are mutually
        // exclusive backend-side). Removal/reset keep using setAppliedGiftcards.
        applyGiftcard: (state, action: PayloadAction<number>) => {
            const existing = state.appliedGiftcards || [];
            if (!existing.includes(action.payload)) {
                state.appliedGiftcards = [...existing, action.payload];
            }
            state.quoteUpdateSource = 'applyGiftcard';
        },
        /** lib/state/Cart.ts:612 — drop one applied gift card; an empty list becomes `undefined`. */
        removeGiftcard: (state, action: PayloadAction<number>) => {
            if (!state.appliedGiftcards) {
                return;
            }
            const updated = state.appliedGiftcards.filter((id) => id !== action.payload);
            state.appliedGiftcards = updated.length > 0 ? updated : undefined;
        },
        /** lib/state/Cart.ts:941 */
        clearSelectedGiftcards: (state) => {
            state.appliedGiftcards = undefined;
        },
        /** lib/state/Cart.ts:944 */
        clearTip: (state) => {
            state.tip = undefined;
        },
        setUserInfo: (state, action: PayloadAction<UserInfo | undefined>) => {
            state.userInfo = action.payload;
        },
        setCartItems: (state, action: PayloadAction<CartLineItem[]>) => {
            state.cartItems = action.payload;
        },
        /**
         * Saga-only. Carries an optional deferred in `meta` (lib/redux/deferred.ts) so
         * `useCart.redux.updateQuote(): Promise<void>` resolves when the quote attempt ends,
         * like the Zustand `updateQuote`. Every existing dispatcher keeps calling it with no
         * argument or a bare `UpdateQuoteParams`; the payload shape is unchanged.
         */
        requestUpdateQuote: {
            reducer: (_state, _action: DeferredAction<UpdateQuoteParams | undefined, void>) => {
                // saga will handle this
            },
            prepare: (payload?: UpdateQuoteParams, deferred?: Deferred<void>) => ({
                payload,
                meta: { deferred }
            })
        },
        applyTip: (state, action: PayloadAction<Tip | undefined>) => {
            state.tip = action.payload;
            state.quoteUpdateSource = undefined;
            // Must update summary with new tip so the next quote request
            // includes the correct tip amount (matches Zustand behavior)
            if (state.quote && action.payload !== undefined) {
                state.summary = Quotes.getOrderSummary(state.quote, {
                    tip: action.payload,
                    appliedCredit: state.appliedCredit
                });
            }
        },
        applyCredit: (state, action: PayloadAction<CurrencyInteger>) => {
            state.appliedCredit = action.payload;
            state.quoteUpdateSource = 'applyCredit';
        },
        applyReward: (state, action: PayloadAction<Reward>) => {
            const reward = action.payload;
            const exists = state.appliedRewards.find((r) => r.id === reward.id);
            if (exists) {
                state.appliedRewards = [];
            } else {
                state.appliedRewards = [reward];
            }
            state.quoteUpdateSource = 'applyReward';
        },
        applyHangReward: (state, action: PayloadAction<HangReward>) => {
            const reward = action.payload;
            const exists = state.appliedHangRewards.find((r) => r.uuid === reward.uuid);
            if (exists) {
                state.appliedHangRewards = [];
            } else {
                state.appliedHangRewards = [reward];
            }
            state.quoteUpdateSource = 'applyHangReward';
        },
        applyPromoCode: (state, action: PayloadAction<string>) => {
            state.cartConfig.promoCode = action.payload;
            state.quoteUpdateSource = 'applyPromoCode';
        },
        clearReusableBowls: (state) => {
            state.cartItems = state.cartItems.map((item) => {
                if ('selections' in item) {
                    const selections = (item as any).selections.map((sel: any) => {
                        if (sel.reusableBowl) {
                            return { ...sel, reusableBowl: false };
                        }
                        return sel;
                    });
                    return { ...item, selections };
                }

                if (item.reusableBowl) {
                    return { ...item, reusableBowl: false };
                }
                return item;
            });
        },
        setGoToCheckoutSignal: (state, action: PayloadAction<string | undefined>) => {
            state.goToCheckoutSignal = action.payload;
        },
        setCartCorrelationId: (state, action: PayloadAction<string>) => {
            state.cartCorrelationId = action.payload;
        },
        updateCartForNewMenu: (state, action: PayloadAction<Menu>) => {
            // saga will handle this
        }
    },
    extraReducers: (builder) => {
        // `clearLoggedInState(clearCart)` (lib/hooks/useAuth.ts:110-119): the rewards always
        // reset; the bag only on a user-initiated logout, never on a session expiry.
        builder.addCase(loggedOut, (state, action) => {
            if (action.payload.clearCart) {
                return initialState;
            }
            Object.assign(state, DefaultCheckoutState);
        });
    }
});

export const {
    addToCart,
    removeFromCart,
    updateCartItem,
    setCartConfig,
    toggleCarbonOffset,
    setPendingReorder,
    setQuoteIsLoading,
    setQuote,
    setQuoteError,
    resetCart,
    resetRewards,
    setAppliedRewards,
    setAppliedHangRewards,
    setAppliedCredit,
    setCredit,
    setSummary,
    setEligibleRewardIds,
    setRewards,
    setCreditRewards,
    setPromoCodeStatus,
    setCartItemErrors,
    setMenuChangeRequiresReview,
    setMenuChangeReport,
    setIsFastReorder,
    setTip,
    setAppliedGiftcards,
    applyGiftcard,
    removeGiftcard,
    clearSelectedGiftcards,
    clearTip,
    setUserInfo,
    setCartItems,
    requestUpdateQuote,
    applyTip,
    applyCredit,
    applyReward,
    applyHangReward,
    applyPromoCode,
    clearReusableBowls,
    setGoToCheckoutSignal,
    setCartCorrelationId,
    updateCartForNewMenu
} = cartSlice.actions;

export const selectCart = (state: { cart: CartState }) => state.cart;
export const selectCartItems = (state: { cart: CartState }) => state.cart.cartItems;
export const selectQuote = (state: { cart: CartState }) => state.cart.quote;
export const selectQuoteError = (state: { cart: CartState }) => state.cart.quoteError;
export const selectCartConfig = (state: { cart: CartState }) => state.cart.cartConfig;
export const selectCartCorrelationId = (state: { cart: CartState }) => state.cart.cartCorrelationId;

export default cartSlice.reducer;
